// Bilingual content registry for all static pages.
// Add a new entry per page; the hook returns the current language map.

import { useTranslation } from "react-i18next";

const CONTENT = {
  // ---------------------- ABOUT US ----------------------
  about: {
    it: {
      eyebrow: "— About Us",
      titleA: "People of",
      titleB: "Europe.",
      subtitle:
        "Tesori d'Europa — Europe's Treasures è un progetto editoriale e culturale dedicato a riscoprire i principi fondativi della Comunità Europea, promuovendo la cultura come strumento di incontro, dialogo e identità condivisa.",
      sec1Eyebrow: "— Il Progetto",
      sec1Title1: "Cultura come",
      sec1Title2: "strumento",
      sec1Title3: "di dialogo.",
      sec1Para1Strong: "Tesori d'Europa – Europe's Treasures",
      sec1Para1: "è un progetto pensato per valorizzare e rivitalizzare i principi fondativi della Comunità Europea, riconoscendo la promozione culturale e l'interazione come strumenti di incontro e dialogo tra i popoli.",
      sec1Para2Pre: "Con lo slogan",
      sec1Para2Em: "People of Europe",
      sec1Para2: ", promuoviamo la cultura europea attraverso le persone e l'autenticità delle loro storie, con l'obiettivo di rafforzare l'identità condivisa dell'UE: donne e uomini che, attraverso cultura, creatività e dialogo, contribuiscono a costruire un'Europa più unita e solidale.",
      asideEyebrow: "— Origini",
      asideTitle: "L'evoluzione internazionale di Tesori d'Italia.",
      asidePara1Pre: "Tesori d'Europa è l'evoluzione internazionale di",
      asidePara1Em: "Tesori d'Italia",
      asidePara1: ", System Brand per l'internazionalizzazione del Made in Italy nel mondo, riconosciuto dalle principali istituzioni italiane. Di proprietà di WJNetwork, opera tramite l'Associazione Tesori d'Italia, che pubblica l'omonima rivista e ha ricevuto patrocini dal Ministero della Cultura, dalla Presidenza del Consiglio, dal Ministero degli Esteri, ENIT, ICE e numerose Regioni italiane.",
      asidePara2Pre: "Tra le iniziative principali, il",
      asidePara2Strong: "TDI Tour",
      asidePara2: "— un viaggio in camper attraverso l'Italia per promuovere territori e tradizioni, che dal 2018 al 2020 ha attraversato quasi l'intero Paese, raggiungendo milioni di utenti in Italia e all'estero.",
    },
    en: {
      eyebrow: "— About Us",
      titleA: "People of",
      titleB: "Europe.",
      subtitle:
        "Tesori d'Europa — Europe's Treasures is an editorial and cultural project dedicated to rediscovering the founding principles of the European Community, promoting culture as a tool for encounter, dialogue and shared identity.",
      sec1Eyebrow: "— The Project",
      sec1Title1: "Culture as",
      sec1Title2: "an instrument",
      sec1Title3: "of dialogue.",
      sec1Para1Strong: "Tesori d'Europa – Europe's Treasures",
      sec1Para1: "is a project designed to enhance and reinvigorate the founding principles of the European Community, recognising cultural promotion and human interaction as tools for encounter and dialogue between peoples.",
      sec1Para2Pre: "With the slogan",
      sec1Para2Em: "People of Europe",
      sec1Para2: ", we promote European culture through people and the authenticity of their stories, with the goal of strengthening the EU's shared identity: women and men who, through culture, creativity and dialogue, help build a more united and supportive Europe.",
      asideEyebrow: "— Origins",
      asideTitle: "The international evolution of Tesori d'Italia.",
      asidePara1Pre: "Tesori d'Europa is the international evolution of",
      asidePara1Em: "Tesori d'Italia",
      asidePara1: ", the System Brand for the internationalisation of Made in Italy worldwide, recognised by leading Italian institutions. Owned by WJNetwork, it operates through the Tesori d'Italia Association, which publishes the homonymous magazine and has received patronage from the Ministry of Culture, the Office of the Prime Minister, the Ministry of Foreign Affairs, ENIT, ICE and numerous Italian Regions.",
      asidePara2Pre: "Among its flagship initiatives, the",
      asidePara2Strong: "TDI Tour",
      asidePara2: "— a camper journey across Italy promoting territories and traditions which, from 2018 to 2020, crossed nearly the entire country, reaching millions of users in Italy and abroad.",
    },
  },

  // ---------------------- MISSION ----------------------
  mission: {
    it: {
      eyebrow: "— Activities / Mission",
      titleA: "Riscoprire l'Europa",
      titleB: "delle persone",
      subtitle:
        "In un momento storico in cui l'Europa affronta una crisi di fiducia senza precedenti, Tesori d'Europa sceglie di riportare al centro ciò che troppo spesso viene dimenticato: le persone che ogni giorno costruiscono l'identità europea.",
      p1Pre: "In un momento storico in cui l'Europa affronta una crisi di fiducia senza precedenti — tensioni geopolitiche, incertezze economiche e una crescente distanza percepita tra istituzioni europee e cittadini —",
      p1Strong: "Tesori d'Europa – Europe's Treasures",
      p1Post: "sceglie di riportare al centro ciò che troppo spesso viene dimenticato:",
      p1Em: "le persone",
      p1End: ".",
      p2: "Donne e uomini che, attraverso creatività, cultura e dialogo, mantengono viva la visione di un continente unito e solidale. Attraverso volti, testimonianze personali, progetti artistici e iniziative culturali, Tesori d'Europa mostra che l'Unione non è un concetto astratto né un costrutto burocratico, ma un mosaico umano fatto di talenti, idee, tradizioni ed energie positive che attraversano i confini.",
      quote: "Unire ciò che la storia ha diviso, creare spazi di cooperazione, salvaguardare la pace e promuovere la dignità e il valore di ogni cittadino europeo.",
      p3Pre: "In un clima in cui disillusione e sfiducia rischiano di oscurare il significato profondo dell'Unione Europea, Tesori d'Europa invita a guardare ancora una volta alle radici del progetto condiviso, ricordando che l'Europa non è solo un accordo politico, ma una",
      p3Strong: "comunità culturale viva, plurale e resiliente",
      p3End: ".",
      p4: "Dare voce ai protagonisti di questa Europa in trasformazione non significa solo raccontare storie: significa contribuire a ricostruire fiducia, alimentare consapevolezza e rafforzare l'idea che un futuro europeo condiviso non è solo possibile, ma è già in atto grazie a chi, ogni giorno, attraverso il proprio lavoro e la propria visione, continua a costruirlo.",
      cta: "Unisciti ai National Project Teams",
    },
    en: {
      eyebrow: "— Activities / Mission",
      titleA: "Rediscovering Europe",
      titleB: "through its people",
      subtitle:
        "At a historic moment when Europe faces an unprecedented crisis of trust, Tesori d'Europa chooses to bring back to the centre what is too often forgotten: the people who build European identity every single day.",
      p1Pre: "At a historic moment when Europe faces an unprecedented crisis of trust — geopolitical tensions, economic uncertainty and a growing perceived distance between European institutions and citizens —",
      p1Strong: "Tesori d'Europa – Europe's Treasures",
      p1Post: "chooses to bring back to the centre what is too often forgotten:",
      p1Em: "the people",
      p1End: ".",
      p2: "Women and men who, through creativity, culture and dialogue, keep alive the vision of a united and supportive continent. Through faces, personal testimonies, artistic projects and cultural initiatives, Tesori d'Europa shows that the Union is neither an abstract concept nor a bureaucratic construct, but a human mosaic of talents, ideas, traditions and positive energies that cross borders.",
      quote: "To unite what history has divided, to create spaces of cooperation, to safeguard peace and to promote the dignity and value of every European citizen.",
      p3Pre: "In a climate where disillusionment and mistrust risk obscuring the deeper meaning of the European Union, Tesori d'Europa invites us to look once again at the roots of the shared project, remembering that Europe is not merely a political agreement, but a",
      p3Strong: "living, plural and resilient cultural community",
      p3End: ".",
      p4: "Giving voice to the protagonists of this transforming Europe is not just about telling stories: it means contributing to rebuilding trust, nurturing awareness and reinforcing the idea that a shared European future is not only possible — it is already underway thanks to those who, every day, through their work and vision, continue to build it.",
      cta: "Join the National Project Teams",
    },
  },

  // ---------------------- CONTACT ----------------------
  contact: {
    it: {
      eyebrow: "— Contact",
      titleA: "Parla con",
      titleB: "noi",
      subtitle:
        "Per collaborazioni, candidature ai national project teams, opportunità editoriali o richieste istituzionali, scrivici. Ti risponderemo dalla nostra redazione centrale di Roma.",
      sideEyebrow: "— Get in touch",
      sideTitle: "Direzione Generale.",
      sideIntro:
        "La nostra direzione generale a Roma è anche redazione centrale. Per ogni richiesta di partnership, candidatura ai National Project Teams o collaborazione editoriale.",
      emailLabel: "Email",
      hqLabel: "Sede principale",
      hqValue: "Roma · Italia",
      hqSub: "Direzione Generale & Redazione Centrale",
      name: "Nome",
      email: "Email",
      message: "Messaggio",
      submitting: "Invio…",
      submit: "Invia messaggio",
      success: "Grazie! Il tuo messaggio è stato inviato. Ti risponderemo presto.",
      errorGeneric: "Si è verificato un errore. Riprova tra qualche istante.",
    },
    en: {
      eyebrow: "— Contact",
      titleA: "Talk to",
      titleB: "us",
      subtitle:
        "For partnerships, applications to the National Project Teams, editorial opportunities or institutional requests, write to us. We will reply from our General Management office in Rome.",
      sideEyebrow: "— Get in touch",
      sideTitle: "General Management.",
      sideIntro:
        "Our general management office in Rome is also the central editorial desk. For any request regarding partnerships, applications to the National Project Teams or editorial collaboration.",
      emailLabel: "Email",
      hqLabel: "Headquarters",
      hqValue: "Rome · Italy",
      hqSub: "General Management & Central Editorial Desk",
      name: "Name",
      email: "Email",
      message: "Message",
      submitting: "Sending…",
      submit: "Send message",
      success: "Thank you! Your message has been sent. We will reply shortly.",
      errorGeneric: "An error occurred. Please try again in a moment.",
    },
  },

  // ---------------------- EVENTS ----------------------
  events: {
    it: {
      eyebrow: "— Activities / Events",
      titleA: "Identità europea,",
      titleB: "celebrazione condivisa",
      subtitle:
        "La sezione Eventi di Tesori d'Europa racconta l'organizzazione e la partecipazione a iniziative internazionali nei 27 Stati membri, con un focus sui contesti culturali e tradizionali che definiscono la ricca identità europea.",
      s1Eyebrow: "— A cosa partecipiamo",
      s1Title1: "Tesori d'Europa partecipa",
      s1Title2: "attivamente a",
      s2Eyebrow: "— Piattaforme strategiche",
      s2Title1: "Tradizioni come",
      s2Title2: "piattaforme strategiche",
      s3Eyebrow: "— Mestieri & territorio",
      s3Title1: "Mestieri antichi,",
      s3Title2: "memoria viva",
      s4Eyebrow: "— Palcoscenico internazionale",
      s4Title1: "Festival internazionali e",
      s4Title2: "audience globale",
      engaged: [
        "Festival tradizionali di rilevanza nazionale e regionale",
        "Grandi festival culturali internazionali",
        "Celebrazioni storiche ed eventi commemorativi",
        "Fiere locali dedicate al cibo tradizionale e all'artigianato",
        "Eventi che promuovono mestieri antichi ed eccellenze artigianali",
        "Iniziative culturali a tutela del patrimonio immateriale",
      ],
      platforms: [
        "Promuovere il dialogo interculturale",
        "Incoraggiare la cooperazione transfrontaliera",
        "Valorizzare l'identità territoriale",
        "Sostenere il turismo culturale e le economie locali",
      ],
      fairs: [
        "Fiere rurali e urbane",
        "Mercatini artigiani e mostre di artigianato",
        "Dimostrazioni di mestieri antichi e tecniche tradizionali",
        "Festival enogastronomici delle eccellenze regionali",
      ],
      international: [
        "Festival di cinema e arti performative",
        "Eventi musicali e folkloristici",
      ],
    },
    en: {
      eyebrow: "— Activities / Events",
      titleA: "European identity,",
      titleB: "shared celebration",
      subtitle:
        "The Events section of Tesori d'Europa tells the story of organising and taking part in international initiatives across the 27 Member States, with a focus on the cultural and traditional contexts that shape Europe's rich identity.",
      s1Eyebrow: "— What we engage in",
      s1Title1: "Tesori d'Europa actively",
      s1Title2: "takes part in",
      s2Eyebrow: "— Strategic platforms",
      s2Title1: "Traditions as",
      s2Title2: "strategic platforms",
      s3Eyebrow: "— Crafts & territory",
      s3Title1: "Ancient crafts,",
      s3Title2: "living memory",
      s4Eyebrow: "— International stage",
      s4Title1: "International festivals and",
      s4Title2: "global audiences",
      engaged: [
        "Traditional festivals of national and regional significance",
        "Major international cultural festivals",
        "Historic celebrations and commemorative events",
        "Local fairs dedicated to traditional food and craftsmanship",
        "Events promoting ancient trades and artisanal excellence",
        "Cultural initiatives that safeguard intangible heritage",
      ],
      platforms: [
        "Promote intercultural dialogue",
        "Encourage cross-border cooperation",
        "Enhance territorial identity",
        "Support cultural tourism and local economies",
      ],
      fairs: [
        "Rural and urban fairs",
        "Artisan markets and craft exhibitions",
        "Demonstrations of ancient trades and traditional techniques",
        "Gastronomic festivals of regional excellence",
      ],
      international: [
        "Film and performing arts festivals",
        "Music and folklore events",
      ],
    },
  },

  // ---------------------- TREASURES 2026 ----------------------
  treasures2026: {
    it: {
      eyebrow: "— Treasures 2026",
      titleA: "Treasures",
      titleB: "2026",
      subtitle:
        "Il programma editoriale e di iniziative dedicate all'Anno Europeo 2026. Stiamo curando il calendario: torna presto per scoprire mostre, residenze, photo contest e percorsi cross-country.",
      sideEyebrow: "— Coming Soon",
      sideTitleA: "Stay",
      sideTitleB: "tuned",
      sidePara1:
        "Treasures 2026 è il punto di sintesi del nostro programma annuale. Un calendario di iniziative pensato per accompagnare il pubblico attraverso i grandi temi culturali del continente — dalle Capitali Europee della Cultura ai festival, dalle nomine alle pubblicazioni.",
      sidePara2:
        "Iscriviti alla newsletter dalla home page per ricevere il calendario completo non appena sarà pubblicato.",
      statCountries: "Paesi",
      statMonths: "Mesi",
      statStories: "Storie",
    },
    en: {
      eyebrow: "— Treasures 2026",
      titleA: "Treasures",
      titleB: "2026",
      subtitle:
        "The editorial programme and initiatives dedicated to the European Year 2026. We are curating the calendar: come back soon to discover exhibitions, residencies, photo contests and cross-country itineraries.",
      sideEyebrow: "— Coming Soon",
      sideTitleA: "Stay",
      sideTitleB: "tuned",
      sidePara1:
        "Treasures 2026 is the synthesis point of our annual programme. A calendar of initiatives designed to accompany audiences through the major cultural themes of the continent — from European Capitals of Culture to festivals, from appointments to publications.",
      sidePara2:
        "Subscribe to the newsletter from the home page to receive the full calendar as soon as it is released.",
      statCountries: "Countries",
      statMonths: "Months",
      statStories: "Stories",
    },
  },
  // ---------------------- HERO-ONLY ENTRIES FOR THE REMAINING PAGES ----------------------
  activitiesIndex: {
    it: { eyebrow: "— Activities", titleA: "Partecipazione, dialogo,", titleB: "crescita", subtitle: "Attraverso corsi di formazione, attività editoriali, comunicazione e iniziative internazionali nei vari Stati membri, Tesori d'Europa pone la partecipazione condivisa al centro della crescita collettiva, in chiave multidisciplinare, inclusiva e accessibile.", cta: "Scopri" },
    en: { eyebrow: "— Activities", titleA: "Participation, dialogue,", titleB: "growth", subtitle: "Through training, editorial activities, communication and international initiatives across the Member States, Tesori d'Europa places shared participation at the heart of collective growth — in a multidisciplinary, inclusive and accessible way.", cta: "Discover" },
  },
  ourTeam: {
    it: {
      eyebrow: "— About Us / Our Team",
      titleA: "Le persone dietro",
      titleB: "Tesori d'Europa",
      subtitle: "Founder, Co-Founder, Board e Consiglio Nazionale: figure di esperienza internazionale che danno corpo alla visione editoriale e culturale del progetto.",
      boardEyebrow: "— Board",
      boardTitle: "Direttivo.",
      boardMembers: "Membri",
      councilEyebrow: "— National Council",
      councilTitle1: "I nostri",
      councilTitle2: "advisor",
      councilMembers: "Membri",
      joinCta: "Unisciti a noi",
    },
    en: {
      eyebrow: "— About Us / Our Team",
      titleA: "The people behind",
      titleB: "Tesori d'Europa",
      subtitle: "Founder, Co-Founder, Board and National Council: figures of international experience who give shape to the editorial and cultural vision of the project.",
      boardEyebrow: "— Board",
      boardTitle: "Board.",
      boardMembers: "Members",
      councilEyebrow: "— National Council",
      councilTitle1: "Our",
      councilTitle2: "advisors",
      councilMembers: "Members",
      joinCta: "Join Us",
    },
  },
  internationalBoard: {
    it: {
      eyebrow: "— About Us / International Board",
      titleA: "Una governance",
      titleB: "internazionale",
      subtitle: "Assembly, Board e National Project Teams: la struttura internazionale che guida Tesori d'Europa nei 27 Paesi dell'Unione.",
      blocks: [
        {
          eyebrow: "— International Assembly",
          titleA: "Il nucleo strategico",
          titleB: "internazionale",
          paragraphs: [
            "Riunisce il Board di Tesori d'Europa, i membri del National Council e i 27 rappresentanti dei National Teams – individuati nei Team Leader o in uno dei cinque membri designati da ciascun team nazionale – garantendo una rappresentanza bilanciata e altamente qualificata di tutti i paesi dell'Unione Europea.",
            "All'Assemblea è affidato il compito di condividere le principali linee di sviluppo del progetto, monitorarne l'andamento complessivo e favorire il dialogo costruttivo tra i diversi contesti nazionali. È al tempo stesso un laboratorio di idee, in cui nuove iniziative vengono proposte e valutate, sia a livello di singolo paese sia in ambiti tematici e settoriali.",
            "Si riunisce una volta all'anno.",
          ],
        },
        {
          eyebrow: "— National Council",
          titleA: "Una guida",
          titleB: "multisettoriale",
          paragraphs: [
            "Il Consiglio Nazionale è un organo consultivo e strategico a supporto del Board e della Direzione Generale del progetto. Opera come struttura di indirizzo, contribuendo alla definizione, allo sviluppo e al consolidamento delle attività di Tesori d'Europa nelle sue varie aree di intervento.",
            "I membri vengono selezionati dal Board sulla base di un profilo professionale elevato, dell'allineamento ai valori del progetto, del contributo concreto allo sviluppo di Tesori d'Italia e/o d'Europa e della capacità di attivare relazioni strategiche a livello nazionale e internazionale.",
            "La composizione del Consiglio rispecchia una natura multisettoriale: cultura, impresa, istituzioni, innovazione, scienza, sanità, comunicazione e relazioni internazionali.",
          ],
        },
        {
          eyebrow: "— National Project Teams",
          titleA: "La forza motrice",
          titleB: "distribuita",
          paragraphs: [
            "I National Project Teams rappresentano la struttura operativa e territoriale del progetto nei 27 paesi UE, fungendo da principale punto di connessione tra la visione centrale e le realtà locali.",
            "Ogni team è composto da 5 membri – selezionati per competenza, motivazione e affinità ai valori del progetto – ed è guidato da un Team Leader. Il loro ruolo è promuovere le attività di Tesori d'Europa nel proprio paese, contribuendo a ricerca, contenuti, diffusione e coinvolgimento di istituzioni, imprese e comunità.",
          ],
        },
      ],
    },
    en: {
      eyebrow: "— About Us / International Board",
      titleA: "An international",
      titleB: "governance",
      subtitle: "Assembly, Board and National Project Teams: the international structure that guides Tesori d'Europa across the 27 Member States of the Union.",
      blocks: [
        {
          eyebrow: "— International Assembly",
          titleA: "The strategic",
          titleB: "international core",
          paragraphs: [
            "It brings together the Tesori d'Europa Board, members of the National Council and the 27 representatives of the National Teams – identified as Team Leaders or one of the five members designated by each national team – ensuring balanced and highly qualified representation of all European Union countries.",
            "The Assembly is tasked with sharing the project's main development lines, monitoring overall progress and fostering constructive dialogue between different national contexts. It is also a laboratory of ideas, in which new initiatives are proposed and assessed, both at the individual country level and within thematic and sectoral areas.",
            "It meets once a year.",
          ],
        },
        {
          eyebrow: "— National Council",
          titleA: "A multisectoral",
          titleB: "guidance",
          paragraphs: [
            "The National Council is an advisory and strategic body supporting the Board and the General Management of the project. It operates as a steering structure, contributing to the definition, development and consolidation of Tesori d'Europa's activities across its various areas of intervention.",
            "Members are selected by the Board based on high professional profile, alignment with the project's values, concrete contribution to the development of Tesori d'Italia and/or d'Europa, and the ability to activate strategic relationships at national and international level.",
            "The composition of the Council reflects a multisectoral nature: culture, enterprise, institutions, innovation, science, healthcare, communications and international relations.",
          ],
        },
        {
          eyebrow: "— National Project Teams",
          titleA: "The distributed",
          titleB: "driving force",
          paragraphs: [
            "The National Project Teams represent the operational and territorial structure of the project across the 27 EU countries, acting as the main point of connection between the central vision and local realities.",
            "Each team is composed of 5 members – selected for expertise, motivation and affinity with the project's values – and is led by a Team Leader. Their role is to promote Tesori d'Europa's activities in their own country, contributing to research, content, dissemination and engagement of institutions, businesses and communities.",
          ],
        },
      ],
    },
  },
  nptTeam: {
    it: {
      eyebrow: "— About Us / National Project Team",
      titleA: "Diventa parte della",
      titleB: "rete europea",
      subtitle: "Cerchiamo professionisti, studiosi e attivisti culturali in ciascuno dei 27 Stati membri. Candidati per entrare nei National Project Teams di Tesori d'Europa.",
      cta: "Candidati ora",
      howEyebrow: "— Come funziona",
      howTitleA: "Un network europeo",
      howTitleB: "aperto",
      howIntro: "Le candidature saranno esaminate e selezionate dal nostro Board, che individuerà i candidati più motivati e allineati con valori, obiettivi e visione di Europe's Treasures.",
      howList: [
        "5 membri per team, incluso un Team Leader",
        "Rappresenta uno dei 27 Stati membri dell'UE",
        "Opera anche da remoto: collaborazione transnazionale e digitale",
        "Promuove territori, comunità e eccellenze culturali",
      ],
    },
    en: {
      eyebrow: "— About Us / National Project Team",
      titleA: "Join the European",
      titleB: "network",
      subtitle: "We are looking for professionals, scholars and cultural activists in each of the 27 Member States. Apply to join the National Project Teams of Tesori d'Europa.",
      cta: "Apply now",
      howEyebrow: "— How it works",
      howTitleA: "An open European",
      howTitleB: "network",
      howIntro: "Applications will be reviewed and selected by our Board, which will identify the most motivated candidates aligned with the values, objectives and vision of Europe's Treasures.",
      howList: [
        "5 members per team, including a Team Leader",
        "Represents one of the 27 EU Member States",
        "Works remotely: transnational and digital collaboration",
        "Promotes territories, communities and cultural excellence",
      ],
    },
  },
  nptApply: {
    it: {
      eyebrow: "— National Project Team / Apply",
      titleA: "Apply to the",
      titleB: "National Project Team",
      subtitle: "Compila il modulo: il team editoriale di Tesori d'Europa valuterà la tua candidatura e ti ricontatterà via email.",
      backLink: "Torna a NPT",
      name: "Nome e Cognome",
      email: "Email",
      country: "Paese UE",
      countryPlaceholder: "Seleziona il tuo paese",
      phone: "Telefono",
      phoneOptional: "(opzionale)",
      profession: "Professione / settore",
      motivation: "Motivazione",
      motivationPlaceholder: "Raccontaci perché vuoi entrare nel team del tuo paese, le tue competenze e cosa puoi portare al progetto.",
      submit: "Invia candidatura",
      submitting: "Invio…",
      success: "Candidatura inviata. Il Board valuterà il tuo profilo e ti contatteremo presto.",
      errorGeneric: "Errore. Riprova tra qualche istante.",
    },
    en: {
      eyebrow: "— National Project Team / Apply",
      titleA: "Apply to the",
      titleB: "National Project Team",
      subtitle: "Fill in the form: the Tesori d'Europa editorial team will review your application and get back to you via email.",
      backLink: "Back to NPT",
      name: "Full name",
      email: "Email",
      country: "EU Country",
      countryPlaceholder: "Select your country",
      phone: "Phone",
      phoneOptional: "(optional)",
      profession: "Profession / field",
      motivation: "Motivation",
      motivationPlaceholder: "Tell us why you want to join your country's team, your skills and what you can bring to the project.",
      submit: "Submit application",
      submitting: "Sending…",
      success: "Application submitted. The Board will review your profile and we will get in touch shortly.",
      errorGeneric: "An error occurred. Please try again in a moment.",
    },
  },
  partners: {
    it: {
      eyebrow: "— About Us / Partners",
      titleA: "Partner, Sponsor e",
      titleB: "Patronage",
      subtitle: "Una rete di istituzioni italiane ed europee, ambasciate, sponsor e patronage che sostengono il progetto Tesori d'Europa nel suo viaggio attraverso i 27 Stati membri.",
      s1Eyebrow: "— Partner",
      s1Title: "I nostri partner strategici.",
      s2Eyebrow: "— Sponsor",
      s2Title: "Sostenitori del progetto.",
      s3Eyebrow: "— Under the Patronage of",
      s3TitleA: "Patrocini",
      s3TitleB: "istituzionali",
    },
    en: {
      eyebrow: "— About Us / Partners",
      titleA: "Partners, Sponsors and",
      titleB: "Patronage",
      subtitle: "A network of Italian and European institutions, embassies, sponsors and patronage supporting Tesori d'Europa on its journey across the 27 Member States.",
      s1Eyebrow: "— Partners",
      s1Title: "Our strategic partners.",
      s2Eyebrow: "— Sponsors",
      s2Title: "Project supporters.",
      s3Eyebrow: "— Under the Patronage of",
      s3TitleA: "Institutional",
      s3TitleB: "patronage",
    },
  },
  newsArchive: {
    it: { eyebrow: "— News Archive", titleA: "Tutte le storie", titleB: "di Tesori d'Europa", subtitle: "Editoriali, nomine del Consiglio Nazionale, eventi e annunci ufficiali. L'archivio completo delle news pubblicate." },
    en: { eyebrow: "— News Archive", titleA: "All stories", titleB: "from Tesori d'Europa", subtitle: "Editorials, National Council appointments, events and official announcements. The complete archive of published news." },
  },
  euToday: {
    it: { eyebrow: "— EU Today", titleA: "L'Europa di oggi,", titleB: "letta in profondità", subtitle: "Heritage, lingue, allargamenti, euro: cinque approfondimenti per leggere l'Unione Europea oggi attraverso storie, dati e prospettive." },
    en: { eyebrow: "— EU Today", titleA: "Today's Europe,", titleB: "read in depth", subtitle: "Heritage, languages, enlargements, the euro: five deep dives to read today's European Union through stories, data and perspectives." },
  },
  euHeritage: {
    it: { eyebrow: "— EU Today / EU Heritage", titleA: "Il patrimonio", titleB: "europeo", subtitle: "27 paesi, una storia condivisa: monumenti, tradizioni e siti UNESCO che raccontano le radici comuni del continente." },
    en: { eyebrow: "— EU Today / EU Heritage", titleA: "Europe's", titleB: "heritage", subtitle: "27 countries, one shared history: monuments, traditions and UNESCO sites that tell the common roots of the continent." },
  },
  euCEE: {
    it: {
      eyebrow: "— EU Today / EU – CEE",
      titleA: "Dalla CEE",
      titleB: "all'Unione",
      subtitle: "Dalla Comunità Economica Europea del 1957 all'Unione Europea di oggi: storia, principi fondativi ed evoluzione istituzionale.",
      s1Title: "Comunità Economica Europea (CEE)",
      s1P1: "Fu istituita ufficialmente con il **Trattato di Roma del 1957**. Inizialmente comprendeva sei paesi fondatori: Belgio, Francia, Germania, Italia, Lussemburgo e Paesi Bassi. L'obiettivo primario era economico: creare un mercato comune, eliminare le barriere commerciali e armonizzare le politiche economiche tra gli Stati membri.",
      s1P2: "La CEE rappresenta dunque il primo passo concreto verso l'integrazione europea, ma restò essenzialmente un progetto economico.",
      s2Title: "Unione Europea (UE)",
      s2P1: "Fu istituita formalmente con il **Trattato di Maastricht del 1992**, segnando una svolta politica importante. L'UE ha assorbito le Comunità Europee (inclusa la CEE) ampliandone le competenze: politica estera e di sicurezza comune, giustizia, diritti dei cittadini, politica sociale e politica monetaria (con l'introduzione dell'euro).",
      s2P2: "L'UE non è solo un mercato, ma un organismo politico e istituzionale, con un Parlamento eletto dai cittadini, un Consiglio Europeo e una Commissione responsabile del processo decisionale.",
      quote: "In sintesi: la CEE è stato l'inizio economico; l'UE ne è l'evoluzione politica e istituzionale.",
      closing: "Comprendere questa differenza aiuta a chiarire i processi decisionali europei, la storia degli allargamenti e il ruolo delle istituzioni nella vita quotidiana di oltre **450 milioni di cittadini**.",
    },
    en: {
      eyebrow: "— EU Today / EU – EEC",
      titleA: "From the EEC",
      titleB: "to the Union",
      subtitle: "From the European Economic Community of 1957 to today's European Union: history, founding principles and institutional evolution.",
      s1Title: "European Economic Community (EEC)",
      s1P1: "It was officially established with the **Treaty of Rome of 1957**. It initially comprised six founding countries: Belgium, France, Germany, Italy, Luxembourg and the Netherlands. Its primary objective was economic: to create a common market, eliminate trade barriers and harmonise economic policies among Member States.",
      s1P2: "The EEC therefore represents the first concrete step towards European integration, but it remained essentially an economic project.",
      s2Title: "European Union (EU)",
      s2P1: "It was formally established with the **Maastricht Treaty of 1992**, marking a major political turning point. The EU absorbed the European Communities (including the EEC), expanding its competences: common foreign and security policy, justice, citizens' rights, social policy and monetary policy (with the introduction of the euro).",
      s2P2: "The EU is not just a market, but a political and institutional body, with a Parliament elected by citizens, a European Council and a Commission responsible for the decision-making process.",
      quote: "In short: the EEC was the economic beginning; the EU is its political and institutional evolution.",
      closing: "Understanding this difference helps clarify European decision-making processes, the history of enlargements and the role of institutions in the daily life of more than **450 million citizens**.",
    },
  },
  roadTo27: {
    it: { eyebrow: "— EU Today / The Road to 27", titleA: "La strada", titleB: "verso i 27", subtitle: "Nove tappe di allargamento, da 6 a 27 Stati membri. La timeline dell'integrazione europea raccontata anno dopo anno." },
    en: { eyebrow: "— EU Today / The Road to 27", titleA: "The road", titleB: "to 27", subtitle: "Nine enlargement waves, from 6 to 27 Member States. The timeline of European integration told year by year." },
  },
  euEuro: {
    it: { eyebrow: "— EU Today / EU – Euro", titleA: "La storia", titleB: "dell'Euro", subtitle: "Dal Trattato di Maastricht all'introduzione fisica delle monete: la storia della moneta unica e dei 21 Stati che l'hanno adottata." },
    en: { eyebrow: "— EU Today / EU – Euro", titleA: "The story", titleB: "of the Euro", subtitle: "From the Maastricht Treaty to the physical introduction of coins: the history of the single currency and the 21 States that have adopted it." },
  },
  euLanguages: {
    it: {
      eyebrow: "— EU Today / Le lingue d'Europa",
      titleA: "Unità senza",
      titleB: "uniformità",
      subtitle: "L'Europa è uno dei continenti con la maggiore ricchezza linguistica al mondo. Oggi questa diversità trova un'espressione istituzionale nell'Unione Europea, che riconosce ufficialmente 24 lingue.",
      p1: "Sebbene l'UE conti 27 Stati membri, il numero di lingue ufficiali è inferiore. Questo perché diversi paesi condividono la stessa lingua nazionale: il tedesco, ad esempio, è ufficiale sia in Germania sia in Austria; il francese in Francia, Belgio e Lussemburgo; il neerlandese nei Paesi Bassi e in Belgio.",
      p2: "Ogni Stato membro ha il diritto di designare una o più lingue ufficiali per l'uso nelle istituzioni europee. È il caso di Belgio (francese, neerlandese e tedesco), Irlanda e Malta. Tutti i cittadini europei hanno il diritto di comunicare con le istituzioni UE nella propria lingua ufficiale.",
      p3: "Accanto alle lingue ufficiali esiste un patrimonio ancora più ampio: **lingue regionali e minoritarie** — catalano, basco, galiziano, sardo, bretone e molte altre — parlate da milioni di persone ma non riconosciute come ufficiali dell'UE.",
      gridEyebrow: "— 24 Lingue Ufficiali",
      gridTitle1: "Le lingue ufficiali",
      gridTitle2: "dell'UE",
      quote: "Le lingue d'Europa non sono soltanto strumenti di comunicazione, ma espressioni dell'identità culturale: l'unità non richiede uniformità.",
      closing: "Per il funzionamento quotidiano delle istituzioni, l'UE utilizza anche alcune \"lingue di lavoro\" — principalmente inglese, francese e tedesco. L'inglese, in particolare, continua a essere ampiamente usato anche dopo la Brexit, grazie alla sua larga diffusione.",
      official: [
        "Bulgaro", "Ceco", "Croato",
        "Danese", "Estone", "Finlandese",
        "Francese", "Gaelico irlandese", "Greco",
        "Inglese", "Italiano",
        "Lettone", "Lituano",
        "Maltese", "Olandese",
        "Polacco", "Portoghese",
        "Rumeno", "Slovacco", "Sloveno",
        "Spagnolo", "Svedese", "Tedesco", "Ungherese",
      ],
    },
    en: {
      eyebrow: "— EU Today / Europe's Languages",
      titleA: "Unity without",
      titleB: "uniformity",
      subtitle: "Europe is one of the world's most linguistically rich continents. Today, this diversity finds institutional expression in the European Union, which officially recognises 24 languages.",
      p1: "Although the EU has 27 Member States, the number of official languages is lower. This is because several countries share the same national language: German, for example, is official in both Germany and Austria; French in France, Belgium and Luxembourg; Dutch in the Netherlands and Belgium.",
      p2: "Each Member State has the right to designate one or more official languages for use in European institutions. This is the case for Belgium (French, Dutch and German), Ireland and Malta. All European citizens have the right to communicate with EU institutions in their own official language.",
      p3: "Alongside the official languages there is an even broader heritage: **regional and minority languages** — Catalan, Basque, Galician, Sardinian, Breton and many others — spoken by millions of people but not recognised as official by the EU.",
      gridEyebrow: "— 24 Official Languages",
      gridTitle1: "The official languages",
      gridTitle2: "of the EU",
      quote: "The languages of Europe are not only tools of communication, but expressions of cultural identity: unity does not require uniformity.",
      closing: "For the daily functioning of institutions, the EU also uses certain \"working languages\" — mainly English, French and German. English, in particular, continues to be widely used even after Brexit, thanks to its broad diffusion.",
      official: [
        "Bulgarian", "Czech", "Croatian",
        "Danish", "Estonian", "Finnish",
        "French", "Irish Gaelic", "Greek",
        "English", "Italian",
        "Latvian", "Lithuanian",
        "Maltese", "Dutch",
        "Polish", "Portuguese",
        "Romanian", "Slovak", "Slovene",
        "Spanish", "Swedish", "German", "Hungarian",
      ],
    },
  },
};

export function usePageContent(key) {
  const { i18n } = useTranslation();
  const lang = i18n.language && CONTENT[key]?.[i18n.language] ? i18n.language : "it";
  return CONTENT[key]?.[lang] || {};
}
